<template>
  <div
    v-on-click-outside="close"
    class="flex flex-col gap-1.5 flex-1 relative"
  >
    <label class="text-xs font-semibold uppercase tracking-wide text-gray-dark font-admin">Events</label>

    <!-- Trigger -->
    <button
      type="button"
      class="flex items-center justify-between w-full border rounded py-2 px-3 bg-white text-sm transition-colors"
      :class="open ? 'border-secondary' : 'border-gray'"
      @click="open = !open"
    >
      <div class="flex items-center gap-2">
        <FontAwesomeIcon
          :icon="faCalendar"
          class="text-secondary text-xs"
        />
        <span>{{ triggerLabel }}</span>
      </div>

      <div class="flex items-center gap-1.5">
        <span
          v-if="model.length > 0 && !allSelected"
          class="bg-secondary text-white text-xs font-bold rounded px-1.5 py-0.5"
        >
          {{ model.length }}
        </span>
        <FontAwesomeIcon
          :icon="faChevronDown"
          class="text-gray-dark text-xs transition-transform duration-150"
          :class="open ? 'rotate-180' : ''"
        />
      </div>
    </button>

    <!-- Dropdown -->
    <div
      v-show="open"
      class="absolute z-20 top-full mt-1 left-0 right-0 bg-white border border-gray rounded-lg"
      style="box-shadow: 0 4px 16px rgba(0,0,0,0.12);"
    >
      <!-- Search -->
      <div class="p-3 border-b border-gray-light">
        <input
          v-model="search"
          type="text"
          placeholder="Search events..."
          class="w-full border border-gray rounded py-1.5 px-3 text-sm focus:border-secondary focus:ring-0"
        >
      </div>

      <!-- All events row (only shown when not searching) -->
      <div
        v-if="!search"
        class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-subtle border-b border-gray-light select-none"
        @click="toggleAll"
      >
        <div class="flex items-center gap-3">
          <input
            type="checkbox"
            :checked="allSelected"
            :indeterminate.prop="someSelected"
            class="accent-secondary w-4 h-4 rounded cursor-pointer"
            readonly
            @click.stop="toggleAll"
          >
          <span class="font-semibold text-sm">All events</span>
        </div>
        <span class="text-sm text-gray-dark">{{ options.length }}</span>
      </div>

      <!-- Event list -->
      <div class="max-h-64 overflow-y-auto">
        <div
          v-if="loading"
          class="px-4 py-3 text-sm text-gray-dark"
        >
          Loading...
        </div>

        <template v-else>
          <div
            v-for="option in options"
            :key="option.id"
            class="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-gray-subtle select-none"
            @click="toggle(option.id)"
          >
            <div class="flex items-center gap-3">
              <input
                type="checkbox"
                :checked="isSelected(option.id)"
                class="accent-secondary w-4 h-4 rounded cursor-pointer"
                readonly
                @click.stop="toggle(option.id)"
              >
              <span class="text-sm">{{ option.name }}</span>
            </div>
            <span class="text-sm text-gray-dark">{{ formatDate(option.date) }}</span>
          </div>

          <div
            v-if="options.length === 0"
            class="px-4 py-3 text-sm text-gray-dark"
          >
            No events found.
          </div>
        </template>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between px-4 py-2.5 border-t border-gray-light">
        <button
          type="button"
          class="text-sm text-secondary font-medium hover:underline"
          @click="selectAll"
        >
          Select all
        </button>
        <button
          type="button"
          class="text-sm text-secondary font-medium hover:underline"
          @click="clearAll"
        >
          Clear
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { watchDebounced } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import { faChevronDown } from '@fortawesome/sharp-regular-svg-icons';

import { useDateFormatter, useHttp } from '@/composables';
import { HttpStatusCode } from 'axios';

const dateFormatter = useDateFormatter();

const model = defineModel({ type: Array, default: () => [] });

const open = ref(false);
const search = ref('');
const options = ref([]);
const loading = ref(false);

const close = () => { open.value = false; };

const loadEvents = async (searchText = null) => {
  loading.value = true;

  const response = await useHttp().get('/events', {
    search: searchText || undefined,
    itemsPerPage: 10,
  });

  if (response.statusCode === HttpStatusCode.Ok) {
    options.value = response.data.items.map(e => ({
      id: e.id,
      name: e.name,
      date: e.date,
    }));

    if (!searchText) {
      model.value = options.value.map(o => o.id);
    }
  }

  loading.value = false;
};

watchDebounced(search, (val) => loadEvents(val || null), { debounce: 300, maxWait: 1000 });

loadEvents();

const isSelected = (id) => model.value.includes(id);

const allSelected = computed(() =>
  options.value.length > 0 && options.value.every(o => isSelected(o.id)),
);

const someSelected = computed(() =>
  options.value.some(o => isSelected(o.id)) && !allSelected.value,
);

const triggerLabel = computed(() =>
  model.value.length === 0 || allSelected.value
    ? 'All events'
    : `${model.value.length} event${model.value.length === 1 ? '' : 's'} selected`,
);

const toggle = (id) => {
  model.value = isSelected(id)
    ? model.value.filter(v => v !== id)
    : [...model.value, id];
};

const toggleAll = () => {
  model.value = allSelected.value ? [] : options.value.map(o => o.id);
};

const selectAll = () => { model.value = options.value.map(o => o.id); };
const clearAll = () => { model.value = []; };

const formatDate = (iso) => dateFormatter.formatShortDate(iso);
</script>
